import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Wrench, ChevronDown, ChevronRight, FileText, Loader2, ClipboardCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const methodLabel = (m, d) => {
  if (!m) return null;
  const base = { cash: 'Cash', check: 'Check', card: 'Card', card_in_person: 'Card', other: 'Other' }[m] || m;
  return d ? `${base} — ${d}` : base;
};
const Row = ({ label, value }) => (value === null || value === undefined || value === '' ? null : (
  <div className="text-xs"><span className="text-[#737373]">{label}: </span><span className="text-[#E5E5E5]">{String(value)}</span></div>
));

const VisitCard = ({ v, navigate }) => {
  const [open, setOpen] = useState(false);
  const d = v.detail || {};
  return (
    <div data-testid={`service-visit-${v.completion_id}`} className="bg-[#0A0A0A] border border-[#262626] rounded-md p-3">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-white font-bold text-sm flex items-center gap-1.5"><Wrench className="w-3.5 h-3.5 text-[#D4AF37]" /> SERVICE COMPLETED — {v.service_type}</p>
          <p className="text-[#A3A3A3] text-xs mt-0.5">{v.date} · {v.technician}{v.work_order_number ? ` · WO #${v.work_order_number}` : ''}</p>
        </div>
      </div>
      {v.work_performed && (
        <div className="mt-2">
          <p className="text-[10px] uppercase tracking-wider text-[#737373]">Work performed</p>
          <p className="text-sm text-[#E5E5E5]">{v.work_performed}</p>
        </div>
      )}
      {/* Invoice group */}
      {v.invoices?.length > 0 && (
        <div className="mt-2 space-y-1">
          <p className="text-[10px] uppercase tracking-wider text-[#737373]">Billing{v.invoices.length > 1 ? ` · Invoice group (${v.invoices.length})` : ''}</p>
          {v.invoices.map((inv) => (
            <div key={inv.id} className="flex items-center justify-between gap-2 bg-[#141414] border border-[#262626] rounded px-2 py-1">
              <div className="text-xs">
                <span className={`font-bold ${inv.kind === 'warranty' ? 'text-blue-300' : 'text-[#D4AF37]'}`}>{inv.kind === 'warranty' ? 'Warranty' : 'Customer'}</span>
                <span className="text-white"> {money(inv.amount)}</span>
                <span className="text-[#737373]"> · {(inv.status || '').toUpperCase()}</span>
                {methodLabel(inv.payment_method, inv.payment_method_detail) && <span className="text-[#A3A3A3]"> · {methodLabel(inv.payment_method, inv.payment_method_detail)}</span>}
                {inv.payment_reference && <span className="text-[#737373]"> · Ref {inv.payment_reference}</span>}
              </div>
              <button data-testid={`service-view-invoice-${inv.id}`} onClick={() => navigate(`/admin/invoices?open=${inv.id}`)} className="text-[10px] text-[#D4AF37] hover:underline whitespace-nowrap">View Invoice</button>
            </div>
          ))}
        </div>
      )}
      <div className="flex items-center gap-3 mt-2">
        <button data-testid={`service-expand-${v.completion_id}`} onClick={() => setOpen((o) => !o)} className="text-xs text-[#A3A3A3] hover:text-white flex items-center gap-1">
          {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />} {open ? 'Hide' : 'View'} Field Completion
        </button>
        {v.appointment_id && (
          <button data-testid={`service-view-job-${v.completion_id}`} onClick={() => navigate('/admin/schedule')} className="text-xs text-[#A3A3A3] hover:text-white flex items-center gap-1"><ClipboardCheck className="w-3.5 h-3.5" /> View Job</button>
        )}
      </div>
      {open && (
        <div data-testid={`service-detail-${v.completion_id}`} className="mt-2 pt-2 border-t border-[#262626] grid grid-cols-2 gap-x-3 gap-y-0.5">
          <Row label="Customer satisfied" value={d.customer_satisfied} />
          <Row label="Cleaned work area" value={d.cleaned_work_area} />
          <Row label="Tested repair" value={d.tested_repair} />
          <Row label="Explained work" value={d.explained_work} />
          <Row label="Review requested" value={d.review_request_sent} />
          <Row label="Signed by" value={d.signed_by} />
          <Row label="Materials" value={d.materials_used} />
          <Row label="Equipment" value={d.equipment_serviced} />
          <Row label="Amount collected" value={d.amount_collected != null ? money(d.amount_collected) : null} />
          <Row label="Payment ref" value={d.payment_reference} />
          <Row label="Billing outcome" value={d.billing_outcome} />
          <Row label="Recommended follow-ups" value={d.recommended_followups} />
          <Row label="Estimate recs" value={d.estimate_recommendations} />
          <Row label="HSP notes" value={d.hsp_notes} />
          <Row label="Payment notes" value={d.payment_notes} />
          <Row label="Internal notes" value={d.internal_notes} />
          <Row label="Photos" value={d.photos?.length ? `${d.photos.length} attached` : null} />
          <Row label="Signature" value={d.has_signature ? 'Captured' : null} />
          <Row label="Submitted" value={v.submitted_at ? `${new Date(v.submitted_at).toLocaleString()} by ${v.submitted_by}` : null} />
        </div>
      )}
    </div>
  );
};

const CustomerServiceHistory = ({ customerId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!customerId) return;
    setLoading(true);
    setError(false);
    axios.get(`${API}/admin/customers/${customerId}/service-history`, { withCredentials: true })
      .then(({ data }) => setData(data))
      .catch(() => { setData({ visits: [] }); setError(true); })
      .finally(() => setLoading(false));
  }, [customerId]);

  return (
    <div data-testid="customer-service-history">
      <p className="text-[#D4AF37] text-xs uppercase tracking-wider font-bold mb-2 flex items-center gap-1.5">
        <Wrench className="w-3.5 h-3.5" /> Service History{data?.count ? ` (${data.count})` : ''}
      </p>
      {loading && <div className="flex items-center gap-2 text-[#A3A3A3] text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>}
      {!loading && error && (
        <p className="text-red-300 text-sm bg-red-500/10 border border-red-500/40 rounded-md p-3">Couldn't load service history. Please retry.</p>
      )}
      {!loading && !error && (!data?.visits || data.visits.length === 0) && (
        <p className="text-[#737373] text-sm bg-[#0A0A0A] border border-[#262626] rounded-md p-3">No completed service visits on file yet.</p>
      )}
      {!loading && data?.visits?.length > 0 && (
        <div className="space-y-2">
          {data.visits.map((v) => <VisitCard key={v.completion_id} v={v} navigate={navigate} />)}
        </div>
      )}
    </div>
  );
};

export default CustomerServiceHistory;
